import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import Button from '../shared/Button'

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isLogging, setIsLogging] = useState(false)
  
  const { login } = useAuth()
  const { addAlert, alerts, removeAlert } = useAlerts()
  const navigate = useNavigate()
  const location = useLocation()

  const from = (location.state as any)?.from?.pathname || '/'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLogging(true)

    const success = await login(username, password)
    if (success) {
      addAlert('success', 'Successfully logged in')
      navigate(from, { replace: true })
    }
    setIsLogging(false)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* Alerts */}
      <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm sm:max-w-sm">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`w-full p-3 sm:p-4 rounded-md shadow-lg ${
              alert.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200'
                : alert.type === 'error'
                ? 'bg-red-100 text-red-800 border border-red-200'
                : alert.type === 'warning'
                ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                : 'bg-blue-100 text-blue-800 border border-blue-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium pr-2">{alert.message}</span>
              <button
                onClick={() => removeAlert(alert.id)}
                className="ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-24 w-auto flex justify-center">
            <img src="/riddell-logo.png" alt="Riddell Logo" className="h-full w-auto object-contain" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to Insite Support Tool
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="username" className="sr-only">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-riddell-red focus:border-riddell-red focus:z-10 sm:text-sm"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-riddell-red focus:border-riddell-red focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Button
              type="submit"
              disabled={isLogging}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-riddell-red hover:bg-riddell-darkred focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-riddell-red disabled:opacity-50"
            >
              {isLogging ? 'Signing in...' : 'Sign in'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LoginPage
